import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { X, Check, Loader2, Sparkles } from "lucide-react";
import {
  ICreateEmailTemplateRequest,
  IEmailTemplate,
  IEmailTemplateLookupItem,
} from "../../../types/Request/AdminRequest";
import { useLazyGetEmailTemplateLookupsQuery } from "../../../redux/actions/Admin/ServiceAdmin";

interface EmailTemplateFormProps {
  template: IEmailTemplate | null;
  onClose: () => void;
  onSubmit: (formData: ICreateEmailTemplateRequest) => Promise<void>;

  onPreview?: (
    templateKey: string,
    variables: Record<string, string>,
  ) => Promise<void>;

  loading?: boolean;
  previewLoading?: boolean;
}

// Helper to auto-generate the dot-separated PascalCase key
const formatToTemplateKey = (name: string, categoryName?: string): string => {
  const sanitizeAndPascal = (str: string) =>
    str
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(".");

  const formattedName = sanitizeAndPascal(name || "");
  const formattedCategory = categoryName ? sanitizeAndPascal(categoryName) : "";

  if (
    formattedCategory &&
    !formattedName.toLowerCase().startsWith(formattedCategory.toLowerCase())
  ) {
    return `${formattedCategory}.${formattedName}`;
  }

  return formattedName;
};

const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({
  template,
  onClose,
  onSubmit,
  onPreview,
  loading = false,
  previewLoading = false,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const [
    getEmailTemplateLookups,
    { data: lookupData, isLoading: isLookupLoading, isError: isLookupError },
  ] = useLazyGetEmailTemplateLookupsQuery();

  // ✅ FIX: Track how many times the modal has opened.
  // This key is passed to ReactQuill to force a full remount on every open.
  const [quillKey] = useState(0);

  // Form State
  const [name, setName] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [subject, setSubject] = useState("");
  const [emailCategoryId, setEmailCategoryId] = useState<number | "">("");
  const [emailLayoutId, setEmailLayoutId] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [plainTextBody, setPlainTextBody] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [categories, setCategories] = useState<IEmailTemplateLookupItem[]>([]);
  const [layouts, setLayouts] = useState<IEmailTemplateLookupItem[]>([]);
  const isSystemTemplate = template?.isSystem === true;
  const isEdit = Boolean(template);

  // Sync state whenever modal opens or active template changes
  useEffect(() => {
    if (template) {
      setName(template.name || "");
      setTemplateKey(template.templateKey || "");
      setEmailCategoryId(template.emailCategoryId ?? "");
      setEmailLayoutId(template.emailLayoutId ?? "");
      setSubject(template.subject || "");
      setDescription(template.description || "");
      setHtmlBody(template.htmlBody || "");
      setPlainTextBody(template.plainTextBody || "");
      setIsActive(template.isActive ?? true);
    } else {
      setName("");
      setTemplateKey("");
      setEmailCategoryId("");
      setEmailLayoutId("");
      setSubject("");
      setDescription("");
      setHtmlBody("");
      setPlainTextBody("");
      setIsActive(true);
    }
  }, [template]);

  useEffect(() => {
    getEmailTemplateLookups();
  }, [getEmailTemplateLookups]);

  useEffect(() => {
    if (lookupData) {
      const lookupCategories = lookupData.categories ?? [];
      const lookupLayouts = lookupData.layouts ?? [];

      setCategories(lookupCategories);
      setLayouts(lookupLayouts);

      if (template) {
        if (template.emailCategoryId) {
          setEmailCategoryId(template.emailCategoryId);
        }

        if (template.emailLayoutId) {
          setEmailLayoutId(template.emailLayoutId);
        } else if (lookupLayouts.length > 0) {
          setEmailLayoutId(lookupLayouts[0].id);
        }
      } else if (lookupLayouts.length > 0) {
        setEmailLayoutId(lookupLayouts[0].id);
      }
    }
  }, [lookupData, template]);

  // Handle Template Name change + Auto-generate key when creating
  const handleNameChange = (newName: string) => {
    setName(newName);
    if (!isEdit) {
      const selectedCategory = categories.find((c) => c.id === emailCategoryId);
      const generated = formatToTemplateKey(newName, selectedCategory?.name);
      setTemplateKey(generated);
    }
  };

  // Handle Category change + Auto-generate key when creating
  const handleCategoryChange = (newCatId: number | "") => {
    setEmailCategoryId(newCatId);
    if (!isEdit) {
      const selectedCategory = categories.find((c) => c.id === newCatId);
      const generated = formatToTemplateKey(name, selectedCategory?.name);
      setTemplateKey(generated);
    }
  };

  // Real-time Text Stats & Automated PlainText Generation
  const editorStats = useMemo(() => {
    const text = htmlBody.replace(/<[^>]*>/g, "").trim();
    const wordCount = text ? text.split(/\s+/).length : 0;
    const charCount = text.length;
    return { text, wordCount, charCount };
  }, [htmlBody]);

  // Image Handler for Rich Text Editor
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          quill.insertEmbed(range?.index || 0, "image", reader.result);
        }
      };
      reader.readAsDataURL(file);
    };
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const derivedPlainText = plainTextBody.trim() || editorStats.text;

    // Ensure template key is generated if creating
    let finalKey = templateKey.trim();
    if (!isEdit && !finalKey) {
      const selectedCategory = categories.find((c) => c.id === emailCategoryId);
      finalKey = formatToTemplateKey(name, selectedCategory?.name);
    }

    const payload: ICreateEmailTemplateRequest = {
      name: name.trim(),
      templateKey: finalKey,
      subject: subject.trim(),
      emailCategoryId: Number(emailCategoryId),
      emailLayoutId: Number(emailLayoutId),
      htmlBody: htmlBody,
      plainTextBody: derivedPlainText,
      description: description.trim(),
      isActive: isActive,
    };

    await onSubmit(payload);
  };

  const isFormInvalid =
    !name.trim() ||
    !templateKey.trim() ||
    !emailCategoryId ||
    !emailLayoutId ||
    !subject.trim() ||
    !htmlBody.trim() ||
    htmlBody === "<p><br></p>" ||
    loading;

  const handlePreview = async () => {
    if (!templateKey.trim()) {
      return;
    }

    await onPreview?.(templateKey.trim(), {
      CustomerName: "John",
      OrderNumber: "ORD-10001",
      RevisionNumber: "REV-1",
      Difference: "AED 50.00",
      RefundAmount: "AED 100.00",
      ExpiryDate: "20 Aug 2026",
      ReviewUrl: "https://yourdomain.com/orders/revision/123",
      PaymentUrl: "https://yourdomain.com/payment",
    });
  };

  const modalContent = (
    <div className="w-full">
      {/* Dynamic CSS scoped for full vertical expansion inside Quill editor */}
      <style>{`
        .quill-resizable-wrapper .quill {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .quill-resizable-wrapper .ql-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }
        .quill-resizable-wrapper .ql-editor {
          flex: 1;
        }
      `}</style>

      <div className="bg-white rounded-xl w-full">
        {/* Modal Body Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto space-y-5 flex-1"
        >
          {/* Top Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Template Name */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">
                Template Name <span className="text-rose-500">*</span>
              </label>

              <input
                type="text"
                placeholder="e.g. Order Confirmation"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                required
              />
            </div>

            {/* Template Key (Read-Only / Auto-Generated for New, Disabled for System Templates) */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">
                Template Key <span className="text-rose-500">*</span>
              </label>

              <input
                type="text"
                placeholder="Auto-generated from template name"
                value={templateKey}
                readOnly={!isEdit || isSystemTemplate}
                disabled={!isEdit || isSystemTemplate}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none bg-slate-50 text-slate-600 cursor-not-allowed select-none transition"
                required
              />

              <p className="text-[10px] text-slate-400">
                {isEdit
                  ? isSystemTemplate
                    ? "System template keys cannot be changed."
                    : "Template key assigned to this template."
                  : "Template key is generated automatically based on the name."}
              </p>
            </div>

            {/* Email Category */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">
                Category <span className="text-rose-500">*</span>
              </label>

              <select
                value={emailCategoryId}
                onChange={(e) =>
                  handleCategoryChange(
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                disabled={isLookupLoading || isLookupError}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {isLookupLoading
                    ? "Loading categories..."
                    : isLookupError
                      ? "Unable to load categories"
                      : "Select Category"}
                </option>

                {categories.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              {isLookupError && (
                <p className="text-[11px] text-rose-500">
                  Unable to load email template categories.
                </p>
              )}
            </div>

            {/* Email Layout */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700">
                Email Layout <span className="text-rose-500">*</span>
              </label>

              <select
                value={emailLayoutId}
                onChange={(e) =>
                  setEmailLayoutId(e.target.value ? Number(e.target.value) : "")
                }
                disabled={isLookupLoading || isLookupError}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {isLookupLoading
                    ? "Loading layouts..."
                    : isLookupError
                      ? "Unable to load layouts"
                      : "Select Email Layout"}
                </option>

                {layouts.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              {isLookupError && (
                <p className="text-[11px] text-rose-500">
                  Unable to load email layouts.
                </p>
              )}
            </div>

            {/* Email Subject */}
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-bold text-slate-700">
                Email Subject <span className="text-rose-500">*</span>
              </label>

              <input
                type="text"
                placeholder="e.g. Your Order {{OrderNumber}} Has Been Placed!"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-700">
              Description
            </label>
            <input
              type="text"
              placeholder="e.g. Template sent to customers upon order confirmation"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition"
            />
          </div>

          {/* Available Variables */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-bold text-slate-700">
                  Available Variables
                </p>

                <p className="text-[10px] text-slate-400 mt-0.5">
                  Click a variable to insert it into the email body.
                </p>
              </div>

              <Sparkles size={15} className="text-primary" />
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                "CustomerName",
                "OrderNumber",
                "RevisionNumber",
                "Difference",
                "RefundAmount",
                "ExpiryDate",
                "ReviewUrl",
                "PaymentUrl",
                "CompanyName",
                "LogoUrl",
                "WebsiteUrl",
                "SupportEmail",
                "SupportPhone",
                "CopyrightText",
                "TermsUrl",
                "PrivacyPolicyUrl",
              ].map((variable) => (
                <button
                  key={variable}
                  type="button"
                  onClick={() => {
                    const quill = quillRef.current?.getEditor();

                    if (!quill) return;

                    const range = quill.getSelection(true);

                    const value = `{{${variable}}}`;

                    quill.insertText(range?.index ?? 0, value);

                    quill.setSelection((range?.index ?? 0) + value.length, 0);
                  }}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[10px] font-semibold text-slate-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition"
                >
                  {`{{${variable}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Rich Text Editor Field (Resizable vertically) */}
          <div className="space-y-2 text-left">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
                <span>Template Body HTML</span>
                <span className="text-rose-500">*</span>
              </label>
              <div className="text-[11px] text-slate-400 font-medium space-x-2">
                <span>{editorStats.wordCount} words</span>
                <span>•</span>
                <span>{editorStats.charCount} chars</span>
              </div>
            </div>

            <div className="quill-resizable-wrapper rounded-xl border border-slate-200 bg-white overflow-auto resize-y min-h-[180px] max-h-[700px] h-[260px] flex flex-col focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
              <ReactQuill
                key={quillKey}
                ref={quillRef}
                theme="snow"
                value={htmlBody}
                onChange={setHtmlBody}
                modules={quillModules}
                placeholder="Write your email body markup here..."
                className="w-full h-full text-xs sm:text-sm flex-1 flex flex-col"
              />
            </div>
          </div>

          {/* Active Status Checkbox */}
          <div className="flex items-center select-none bg-slate-50 border border-slate-200 p-3.5 rounded-xl cursor-pointer hover:bg-slate-100/60 transition-colors">
            <input
              id="isTemplateActiveToggle"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary cursor-pointer"
            />
            <label
              htmlFor="isTemplateActiveToggle"
              className="ml-3 text-xs font-bold text-slate-800 cursor-pointer flex flex-col leading-tight"
            >
              <span>Enable Template</span>
              <span className="text-[11px] text-slate-400 font-normal mt-0.5">
                When enabled, system services can send automated notifications
                using this layout.
              </span>
            </label>
          </div>
  {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6 bg-white">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2 rounded-xl text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-100 transition cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={loading || previewLoading || !templateKey.trim()}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {previewLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Previewing...</span>
                </>
              ) : (
                "Preview"
              )}
            </button>
            <button
              type="submit"
              disabled={isFormInvalid}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-6 py-2 rounded-xl shadow-sm transition disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check size={14} />
                  <span>{template ? "Update Template" : "Save Template"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return modalContent;
};

export default EmailTemplateForm;